/**
 * Smart Job Intake Page - Elite UI
 * ============================================================
 * Premium teal-branded smart intake UI
 * - Internal / client / mobile-friendly in one component
 * - No random category colours - unified teal theme
 * - Strong hierarchy, fast input, mobile-first
 * - Connected to real backend APIs
 * ============================================================
 */
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

const ISSUE_OPTIONS = [
  { key: "electrical_fault", label: "Electrical", icon: "⚡" },
  { key: "gas_issue", label: "Gas", icon: "🔥" },
  { key: "plumbing_leak", label: "Plumbing", icon: "💧" },
  { key: "boiler_issue", label: "Boiler", icon: "🌡️" },
  { key: "fire_alarm_issue", label: "Fire Alarm", icon: "🔔" },
  { key: "drainage_issue", label: "Drainage", icon: "🚿" },
  { key: "legionella_issue", label: "Legionella", icon: "🧪" },
  { key: "structural_issue", label: "Structural", icon: "🏗️" },
  { key: "general_repair", label: "Repair", icon: "🛠️" },
  { key: "damage_report", label: "Damage", icon: "⚠️" },
  { key: "ppm_task", label: "PPM Task", icon: "📅" },
  { key: "quoted_work", label: "Quoted Work", icon: "📋" },
  { key: "inspection_required", label: "Inspection", icon: "🔍" },
  { key: "unknown_other", label: "Other", icon: "❔" },
];

const PRIORITIES = [
  { key: "urgent", label: "URGENT", tone: "critical" },
  { key: "high", label: "High", tone: "warning" },
  { key: "medium", label: "Medium", tone: "brand" },
  { key: "low", label: "Low", tone: "neutral" },
];

function classNames(...items) {
  return items.filter(Boolean).join(" ");
}

function SectionCard({ title, subtitle, children, right }) {
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[22px] font-semibold tracking-[-0.02em] text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

function InfoPill({ children, variant = "default" }) {
  const variants = {
    default: "border-teal-200 bg-teal-50 text-teal-700",
    warning: "border-amber-200 bg-amber-50 text-amber-700",
    success: "border-green-200 bg-green-50 text-green-700",
  };
  return (
    <div className={classNames("rounded-full border px-3 py-1 text-xs font-medium", variants[variant])}>
      {children}
    </div>
  );
}

function PreviewRow({ label, value, highlight = false }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-3 last:border-b-0">
      <div className="text-sm text-slate-500">{label}</div>
      <div className={classNames("text-right text-sm font-medium", highlight ? "text-teal-700" : "text-slate-900")}>
        {value || "-"}
      </div>
    </div>
  );
}

function UploadTile({ label, hint, onChange, files = [] }) {
  return (
    <label className="group flex cursor-pointer flex-col rounded-[24px] border border-dashed border-slate-300 bg-slate-50 p-4 transition hover:border-teal-400 hover:bg-teal-50">
      <div className="mb-2 text-base font-medium text-slate-800">{label}</div>
      <div className="text-sm text-slate-500">{hint}</div>
      <input type="file" className="hidden" multiple accept="image/*" onChange={onChange} />
      <div className="mt-4 inline-flex w-fit rounded-full bg-white px-4 py-2 text-sm font-medium text-teal-700 ring-1 ring-inset ring-teal-200">
        {files.length > 0 ? `${files.length} file(s) selected` : "Upload"}
      </div>
    </label>
  );
}

export default function SmartJobIntakePage() {
  const navigate = useNavigate();
  
  // Data from API
  const [parks, setParks] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  
  // Form state
  const [step, setStep] = useState(1);
  const [parkId, setParkId] = useState("");
  const [parkSearch, setParkSearch] = useState("");
  const [showParkDropdown, setShowParkDropdown] = useState(false);
  const [unitName, setUnitName] = useState("");
  const [issueType, setIssueType] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  const [showMore, setShowMore] = useState(false);

  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [accessNotes, setAccessNotes] = useState("");
  const [preferredContractor, setPreferredContractor] = useState("");
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [applyAutoAssignment, setApplyAutoAssignment] = useState(true);
  
  // Preview from API
  const [apiPreview, setApiPreview] = useState(null);

  // Fetch options on mount
  useEffect(() => {
    fetchOptions();
  }, []);

  const fetchOptions = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/jobs/intake/options`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.ok) {
        setParks(response.data.parks || []);
        setContractors(response.data.contractors || []);
      }
    } catch (err) {
      console.error("Failed to fetch options:", err);
      toast.error("Failed to load form options");
    } finally {
      setLoading(false);
    }
  };

  const selectedPark = useMemo(
    () => parks.find((p) => p.id === parkId) || null,
    [parks, parkId]
  );

  const selectedIssue = useMemo(
    () => ISSUE_OPTIONS.find((i) => i.key === issueType) || null,
    [issueType]
  );

  // Filter parks by search
  const filteredParks = parkSearch
    ? parks.filter(
        (p) =>
          p.name?.toLowerCase().includes(parkSearch.toLowerCase()) ||
          p.postcode?.toLowerCase().includes(parkSearch.toLowerCase())
      )
    : parks;

  // Local preview (shown immediately)
  const localPreview = useMemo(() => {
    if (!selectedIssue) return null;

    const tradeMap = {
      electrical_fault: "Electrical",
      gas_issue: "Gas",
      plumbing_leak: "Plumbing",
      boiler_issue: "Gas / Boiler",
      fire_alarm_issue: "Fire & Security",
      drainage_issue: "Drainage",
      legionella_issue: "Water Hygiene",
      structural_issue: "Building",
      general_repair: "General",
      damage_report: "General / Survey",
      ppm_task: "PPM",
      quoted_work: "Quoted Work",
      inspection_required: "Survey",
      unknown_other: "Review Required",
    };

    const jobTypeMap = {
      electrical_fault: "Reactive",
      gas_issue: "Reactive",
      plumbing_leak: "Reactive",
      boiler_issue: "Reactive",
      fire_alarm_issue: "Reactive / Compliance",
      drainage_issue: "Reactive",
      legionella_issue: "Compliance",
      structural_issue: "Reactive",
      general_repair: "Quoted Work",
      damage_report: "Quoted Work",
      ppm_task: "PPM",
      quoted_work: "Quoted Work",
      inspection_required: "Survey",
      unknown_other: "Review Required",
    };

    const dueMap = {
      urgent: "Same day / emergency SLA",
      high: "1 working day",
      medium: "3 working days",
      low: "5 working days",
    };

    const certRequired = ["gas_issue", "boiler_issue", "fire_alarm_issue", "legionella_issue"].includes(issueType);

    return {
      trade: tradeMap[issueType] || "General",
      jobType: jobTypeMap[issueType] || "Review Required",
      due: dueMap[priority] || "3 working days",
      certificateRequired: certRequired ? "Yes" : "No",
      ramsRequired: "Yes",
    };
  }, [selectedIssue, issueType, priority]);

  // Merged preview (API + local)
  const preview = useMemo(() => {
    if (apiPreview) {
      return {
        trade: apiPreview.classification?.trade_type || localPreview?.trade,
        jobType: apiPreview.classification?.job_type || localPreview?.jobType,
        due: apiPreview.due_dates?.sla_label || localPreview?.due,
        dueDate: apiPreview.due_dates?.due_date,
        certificateRequired: apiPreview.requirements?.certificate_required ? "Yes" : "No",
        ramsRequired: apiPreview.requirements?.rams_required ? "Yes" : "No",
        suggestedContractor: apiPreview.contractor_suggestion?.suggested_contractor_name || "Best available",
        confidenceScore: apiPreview.contractor_suggestion?.confidence_score,
        autoAssignable: apiPreview.contractor_suggestion?.auto_assignable,
        sameSiteJobs: apiPreview.context?.same_site_open_jobs_count || 0,
        nearbyJobs: apiPreview.context?.nearby_open_jobs_count || 0,
        duplicateWarning: apiPreview.context?.duplicate_warning,
        duplicateMessage: apiPreview.context?.duplicate_message,
      };
    }
    return {
      ...localPreview,
      suggestedContractor: preferredContractor || "Best available contractor",
      sameSiteJobs: 0,
      nearbyJobs: 0,
      duplicateWarning: false,
    };
  }, [apiPreview, localPreview, preferredContractor]);

  const canContinueStep1 = !!issueType && !!priority;
  const canContinueStep2 = !!parkId && !!description.trim();

  const selectPark = (park) => {
    setParkId(park.id);
    setParkSearch(park.name);
    setShowParkDropdown(false);
  };

  function nextStep() {
    if (step === 1 && canContinueStep1) setStep(2);
    if (step === 2 && canContinueStep2) setStep(3);
  }

  function prevStep() {
    setStep((s) => Math.max(1, s - 1));
  }

  // Call preview API
  const handlePreview = async () => {
    if (!parkId || !issueType) return;

    setPreviewing(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        park_id: parkId,
        park_name: selectedPark?.name,
        unit_name: unitName,
        issue_type: issueType,
        description: description || `${selectedIssue?.label} issue reported`,
        priority,
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_email: contactEmail,
        access_notes: accessNotes,
        preferred_contractor_id: preferredContractor,
        apply_auto_assignment: applyAutoAssignment,
      };

      const response = await axios.post(`${API}/api/jobs/intake/preview`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.ok) {
        setApiPreview(response.data);
        setStep(3);
      } else {
        toast.error(response.data.error || "Preview failed");
      }
    } catch (err) {
      console.error("Preview error:", err);
      toast.error(err.response?.data?.detail || "Failed to preview job");
    } finally {
      setPreviewing(false);
    }
  };

  // Create job
  const handleCreate = async () => {
    if (!parkId || !issueType || !description.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        park_id: parkId,
        park_name: selectedPark?.name,
        unit_name: unitName,
        issue_type: issueType,
        description: description || `${selectedIssue?.label} issue reported`,
        priority,
        contact_name: contactName,
        contact_phone: contactPhone,
        contact_email: contactEmail,
        access_notes: accessNotes,
        preferred_contractor_id: preferredContractor,
        apply_auto_assignment: applyAutoAssignment,
        override_duplicate_warning: false,
      };

      const response = await axios.post(`${API}/api/jobs/intake/create`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.data.ok) {
        toast.success(`Job created: ${response.data.job_reference}`);
        navigate(`/fm/jobs/${response.data.job_id}`);
      } else if (response.data.require_duplicate_override) {
        // Show duplicate warning
        if (window.confirm(`${response.data.error}\n\nDo you want to create the job anyway?`)) {
          const retryResponse = await axios.post(
            `${API}/api/jobs/intake/create`,
            { ...payload, override_duplicate_warning: true },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (retryResponse.data.ok) {
            toast.success(`Job created: ${retryResponse.data.job_reference}`);
            navigate(`/fm/jobs/${retryResponse.data.job_id}`);
          } else {
            toast.error(retryResponse.data.error || "Failed to create job");
          }
        }
      } else {
        toast.error(response.data.error || "Failed to create job");
      }
    } catch (err) {
      console.error("Create error:", err);
      toast.error(err.response?.data?.detail || "Failed to create job");
    } finally {
      setCreating(false);
    }
  };

  const progressWidth = `${(step / 3) * 100}%`;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="smart-job-intake-page">
      <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6 lg:px-8">
        <div className="grid gap-5 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-5">
            {/* Header */}
            <div className="overflow-hidden rounded-[30px] bg-gradient-to-r from-teal-700 to-teal-600 p-5 text-white shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-[26px] font-semibold tracking-[-0.03em]">Quick Report</div>
                  <div className="mt-1 text-sm text-teal-100">Step {step} of 3</div>
                </div>
                <div className="rounded-full bg-white/15 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
                  {selectedIssue ? selectedIssue.label : "Select Issue"}
                </div>
              </div>

              <div className="mt-5 h-2 w-full overflow-hidden rounded-full bg-white/20">
                <div
                  className="h-full rounded-full bg-white transition-all duration-300"
                  style={{ width: progressWidth }}
                />
              </div>
            </div>

            {/* Step 1: Issue Type */}
            {step === 1 && (
              <SectionCard
                title="What's the issue?"
                subtitle="Select the main issue type first so the system can classify the job correctly."
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                  {ISSUE_OPTIONS.map((item) => {
                    const selected = issueType === item.key;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => setIssueType(item.key)}
                        className={classNames(
                          "rounded-[22px] border p-4 text-left transition-all duration-150",
                          "focus:outline-none focus:ring-2 focus:ring-teal-500",
                          selected
                            ? "border-teal-600 bg-teal-50 shadow-sm"
                            : "border-slate-200 bg-white hover:border-teal-300 hover:bg-teal-50/50"
                        )}
                        data-testid={`issue-type-${item.key}`}
                      >
                        <div
                          className={classNames(
                            "mb-3 flex h-10 w-10 items-center justify-center rounded-full text-lg",
                            selected ? "bg-teal-600 text-white" : "bg-slate-100 text-slate-700"
                          )}
                        >
                          {item.icon}
                        </div>
                        <div className={classNames("text-sm font-semibold", selected ? "text-teal-800" : "text-slate-900")}>
                          {item.label}
                        </div>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-6">
                  <div className="mb-3 text-sm font-medium text-slate-600">Priority</div>
                  <div className="grid grid-cols-4 gap-3">
                    {PRIORITIES.map((p) => {
                      const selected = priority === p.key;
                      const tone =
                        p.tone === "critical"
                          ? selected
                            ? "bg-red-600 text-white border-red-600"
                            : "bg-white text-red-700 border-red-200"
                          : p.tone === "warning"
                          ? selected
                            ? "bg-amber-500 text-white border-amber-500"
                            : "bg-white text-amber-700 border-amber-200"
                          : p.tone === "brand"
                          ? selected
                            ? "bg-teal-600 text-white border-teal-600"
                            : "bg-white text-teal-700 border-teal-200"
                          : selected
                          ? "bg-slate-700 text-white border-slate-700"
                          : "bg-white text-slate-700 border-slate-200";

                      return (
                        <button
                          key={p.key}
                          type="button"
                          onClick={() => setPriority(p.key)}
                          className={classNames(
                            "rounded-full border px-3 py-2.5 text-sm font-semibold transition",
                            tone
                          )}
                          data-testid={`priority-${p.key}`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    type="button"
                    disabled={!canContinueStep1}
                    onClick={nextStep}
                    className={classNames(
                      "rounded-full px-6 py-3 text-sm font-semibold transition",
                      canContinueStep1
                        ? "bg-teal-600 text-white hover:bg-teal-700"
                        : "bg-slate-200 text-slate-400"
                    )}
                    data-testid="step1-continue-btn"
                  >
                    Continue
                  </button>
                </div>
              </SectionCard>
            )}

            {/* Step 2: Location */}
            {step === 2 && (
              <SectionCard
                title="Where is it?"
                subtitle="Enter the site and unit details. Keep it short and clear."
              >
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="relative">
                    <label className="mb-2 block text-sm font-medium text-slate-700">Park / Site *</label>
                    <input
                      type="text"
                      value={parkSearch}
                      onChange={(e) => {
                        setParkSearch(e.target.value);
                        setShowParkDropdown(true);
                        if (!e.target.value) {
                          setParkId("");
                        }
                      }}
                      onFocus={() => setShowParkDropdown(true)}
                      placeholder="Search parks..."
                      className="w-full rounded-[20px] border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-teal-500"
                      data-testid="park-search-input"
                    />
                    {showParkDropdown && filteredParks.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full rounded-[16px] border border-slate-200 bg-white shadow-lg max-h-48 overflow-y-auto">
                        {filteredParks.slice(0, 8).map((park) => (
                          <button
                            key={park.id}
                            type="button"
                            onClick={() => selectPark(park)}
                            className={classNames(
                              "w-full px-4 py-3 text-left border-b border-slate-100 last:border-0 hover:bg-teal-50",
                              parkId === park.id ? "bg-teal-50" : ""
                            )}
                          >
                            <div className="font-medium text-slate-800">{park.name}</div>
                            {park.postcode && (
                              <div className="text-xs text-slate-500">{park.postcode}</div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-slate-700">Plot / Unit</label>
                    <input
                      value={unitName}
                      onChange={(e) => setUnitName(e.target.value)}
                      placeholder="e.g. Plot 4"
                      className="w-full rounded-[20px] border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-teal-500"
                      data-testid="unit-name-input"
                    />
                  </div>
                </div>

                {selectedPark ? (
                  <div className="mt-4 rounded-[22px] border border-teal-200 bg-teal-50 p-4 text-sm text-teal-800">
                    <div className="font-semibold">{selectedPark.name}</div>
                    {selectedPark.postcode && <div className="mt-1">{selectedPark.postcode} • {selectedPark.region}</div>}
                  </div>
                ) : null}

                <div className="mt-4">
                  <label className="mb-2 block text-sm font-medium text-slate-700">Describe the issue *</label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what's wrong in plain language. Keep it short but specific."
                    className="min-h-[120px] w-full rounded-[24px] border border-slate-300 bg-white px-4 py-4 text-sm text-slate-900 outline-none focus:border-teal-500"
                    data-testid="description-input"
                  />
                </div>

                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <UploadTile
                    label="Add Photo"
                    hint="Helpful but optional. Good for quick triage and contractor context."
                    files={uploadedFiles}
                    onChange={(e) => setUploadedFiles(Array.from(e.target.files || []))}
                  />

                  <div className="rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                    <button
                      type="button"
                      onClick={() => setShowMore((v) => !v)}
                      className="w-full rounded-full bg-white px-4 py-3 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-slate-200"
                    >
                      {showMore ? "Hide more details" : "More details"}
                    </button>

                    {showMore ? (
                      <div className="mt-4 space-y-3">
                        <input
                          value={contactName}
                          onChange={(e) => setContactName(e.target.value)}
                          placeholder="Contact name"
                          className="w-full rounded-[18px] border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-teal-500"
                        />
                        <input
                          value={contactPhone}
                          onChange={(e) => setContactPhone(e.target.value)}
                          placeholder="Contact phone"
                          className="w-full rounded-[18px] border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-teal-500"
                        />
                        <input
                          value={contactEmail}
                          onChange={(e) => setContactEmail(e.target.value)}
                          placeholder="Contact email"
                          className="w-full rounded-[18px] border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-teal-500"
                        />
                        <textarea
                          value={accessNotes}
                          onChange={(e) => setAccessNotes(e.target.value)}
                          placeholder="Access notes"
                          className="min-h-[90px] w-full rounded-[18px] border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-teal-500"
                        />
                        <select
                          value={preferredContractor}
                          onChange={(e) => setPreferredContractor(e.target.value)}
                          className="w-full rounded-[18px] border border-slate-300 bg-white px-4 py-3 text-sm outline-none focus:border-teal-500"
                        >
                          <option value="">No preferred contractor</option>
                          {contractors.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={applyAutoAssignment}
                            onChange={(e) => setApplyAutoAssignment(e.target.checked)}
                            className="rounded border-slate-300"
                          />
                          Auto-assign to best available contractor
                        </label>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="rounded-full bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-700"
                  >
                    Back
                  </button>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handlePreview}
                      disabled={!canContinueStep2 || previewing}
                      className={classNames(
                        "rounded-full px-5 py-3 text-sm font-semibold flex items-center gap-2",
                        canContinueStep2
                          ? "bg-white text-teal-700 ring-1 ring-inset ring-teal-300"
                          : "bg-slate-200 text-slate-400"
                      )}
                      data-testid="preview-btn"
                    >
                      {previewing && <Loader2 className="h-4 w-4 animate-spin" />}
                      Preview Job
                    </button>
                    <button
                      type="button"
                      onClick={nextStep}
                      disabled={!canContinueStep2}
                      className={classNames(
                        "rounded-full px-6 py-3 text-sm font-semibold",
                        canContinueStep2
                          ? "bg-teal-600 text-white hover:bg-teal-700"
                          : "bg-slate-200 text-slate-400"
                      )}
                      data-testid="step2-continue-btn"
                    >
                      Continue
                    </button>
                  </div>
                </div>
              </SectionCard>
            )}

            {/* Step 3: Review */}
            {step === 3 && (
              <SectionCard
                title="Review and create"
                subtitle="The system has classified the job and prepared the recommended workflow."
              >
                <div className="flex flex-wrap gap-2">
                  <InfoPill>{selectedIssue ? selectedIssue.label : "No issue selected"}</InfoPill>
                  <InfoPill>{preview?.jobType || "No job type"}</InfoPill>
                  <InfoPill>{preview?.trade || "No trade"}</InfoPill>
                  <InfoPill>{preview?.due || "No SLA"}</InfoPill>
                </div>

                {preview?.duplicateWarning && (
                  <div className="mt-4 rounded-[22px] border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
                    <div className="font-semibold">Possible duplicate detected</div>
                    <div className="mt-1">{preview.duplicateMessage}</div>
                  </div>
                )}

                {preview?.sameSiteJobs > 0 && (
                  <div className="mt-4 rounded-[22px] border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
                    <div className="font-semibold">{preview.sameSiteJobs} open job(s) at this site</div>
                    <div className="mt-1">Consider grouping work if attending the same location</div>
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={prevStep}
                    className="rounded-full bg-slate-100 px-5 py-3 text-sm font-semibold text-slate-700"
                  >
                    Back
                  </button>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={handlePreview}
                      disabled={previewing}
                      className="rounded-full bg-white px-5 py-3 text-sm font-semibold text-teal-700 ring-1 ring-inset ring-teal-300 flex items-center gap-2"
                    >
                      {previewing && <Loader2 className="h-4 w-4 animate-spin" />}
                      Refresh Preview
                    </button>
                    <button
                      type="button"
                      onClick={handleCreate}
                      disabled={creating}
                      className="rounded-full bg-teal-600 px-6 py-3 text-sm font-semibold text-white hover:bg-teal-700 flex items-center gap-2"
                      data-testid="create-btn"
                    >
                      {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                      Create Job
                    </button>
                  </div>
                </div>
              </SectionCard>
            )}
          </div>

          {/* Preview sidebar */}
          <div className="space-y-5 hidden lg:block">
            <SectionCard
              title="Smart Preview"
              subtitle="This is what the system is building from your intake."
              right={step === 3 ? <InfoPill variant="success">Ready to create</InfoPill> : null}
            >
              <PreviewRow label="Park / Site" value={selectedPark?.name} />
              <PreviewRow label="Plot / Unit" value={unitName} />
              <PreviewRow label="Issue Type" value={selectedIssue?.label} />
              <PreviewRow label="Priority" value={priority ? priority.toUpperCase() : "-"} />
              <PreviewRow label="Job Type" value={preview?.jobType} highlight />
              <PreviewRow label="Trade" value={preview?.trade} />
              <PreviewRow label="Suggested SLA / Due" value={preview?.due} highlight />
              {preview?.dueDate && (
                <PreviewRow
                  label="Due Date"
                  value={new Date(preview.dueDate).toLocaleDateString("en-GB", {
                    weekday: "short",
                    day: "numeric",
                    month: "short",
                  })}
                  highlight
                />
              )}
              <PreviewRow label="Certificate Required" value={preview?.certificateRequired} />
              <PreviewRow label="RAMS Required" value={preview?.ramsRequired} />
              <PreviewRow label="Suggested Contractor" value={preview?.suggestedContractor} />
              {preview?.confidenceScore !== undefined && (
                <PreviewRow
                  label="Assignment Confidence"
                  value={`${Math.round(preview.confidenceScore * 100)}%`}
                />
              )}
              <PreviewRow label="Same-Site Open Jobs" value={preview?.sameSiteJobs ?? 0} />
              <PreviewRow label="Nearby Open Jobs" value={preview?.nearbyJobs ?? 0} />
            </SectionCard>

            <SectionCard
              title="Why this works"
              subtitle="The intake is intentionally minimal so jobs can be raised quickly without losing structure."
            >
              <ul className="space-y-3 text-sm text-slate-600">
                <li>• Fast enough for real field use</li>
                <li>• Structured enough for control and reporting</li>
                <li>• Auto-classifies by issue type and priority</li>
                <li>• Feeds smart assignment, compliance, and contractor actions</li>
              </ul>
            </SectionCard>
          </div>
        </div>
      </div>
    </div>
  );
}
